import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import AutoSizer from 'components/AutoSizer';
import ToggleDropdown from 'nav/ToggleDropdown';
import BulkCreateStageEntryReviewModal from 'notebook/reviews/BulkCreateStageEntryReviewModal';
import UpdateFulfillmentStatusModal from 'requests/fulfillments/UpdateFulfillmentStatusModal';
import Breadcrumbs from 'ui-library/Breadcrumbs';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Header1} from 'ui-library/Headers';
import SettingsIcon from 'ui-library/icons/SettingsIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import runModal from 'ui-library/runModal';
import WorkflowSettingsModal from 'workflows/modals/WorkflowSettingsModal';
import {StageRunGraph} from 'workflows/visualization/TaggedNodeGraph';
import WorkflowVisualization from 'workflows/visualization/WorkflowVisualization';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_RUN_FULL_SHAPE,
  StageConfiguration,
  StageEntrySummary,
  StageRun,
  Workflow,
  WORKFLOW_FULL_SHAPE,
  WORKFLOW_STAGE_FULL_SHAPE,
  WorkflowStage,
} from 'workflows/WorkflowConstants';

import {WorkflowsCrumbLink} from './components/WorkflowCrumbLinks';
import WorkflowDescription from './components/WorkflowDescription';

interface WorkflowDetailsHeaderProps {
  workflow: Workflow;
  workflowStages: Array<WorkflowStage>;
  stageRunGraph: StageRunGraph;
  stageIdToStageRuns: {[id: string]: Array<StageRun>};
  stageIdToStageConfiguration: {[id: string]: StageConfiguration};
  isExpanded: boolean;
}

interface State {
  isSettingsModalOpen: boolean;
}

class WorkflowDetailsHeader extends React.Component<WorkflowDetailsHeaderProps, State> {
  static propTypes = {
    workflow: WORKFLOW_FULL_SHAPE.isRequired,
    workflowStages: PropTypes.arrayOf(WORKFLOW_STAGE_FULL_SHAPE.isRequired).isRequired,
    stageRunGraph: PropTypes.object.isRequired,
    stageIdToStageRuns: PropTypes.objectOf(PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE)).isRequired,
    stageIdToStageConfiguration: PropTypes.objectOf(STAGE_CONFIGURATION_FULL_SHAPE).isRequired,

    isExpanded: PropTypes.bool.isRequired,
  };

  state: State = {
    isSettingsModalOpen: false,
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleClickSettingsIcon = () => {
    this.setState({
      isSettingsModalOpen: true,
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleRequestCloseModal = () => {
    this.setState({
      isSettingsModalOpen: false,
    });
  };

  _shouldShowSettings(): boolean {
    return this.props.workflow.permissions.writable;
  }

  _getAllStageEntryIds(): Array<string> {
    return ([] as Array<StageEntrySummary>)
      .concat(...this.props.workflowStages.map((stage) => stage.stageEntries))
      .map((stageEntry) => stageEntry.id);
  }

  _wasInitializedFromRequest(): boolean {
    return this.props.workflow.initializationDatas.length !== 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateFulfillmentStatuses = async () => {
    await runModal(UpdateFulfillmentStatusModal, {workflow: this.props.workflow});
  };

  render(): JSX.Element {
    return (
      <div className="workflows-workflowDetailsHeader">
        <div
          className={classNames('workflows-workflowDetailsHeader-column', {
            'is-expanded': this.props.isExpanded,
          })}
        >
          <div className="workflows-workflowDetailsHeader-row1">
            {!this.props.isExpanded && <ToggleDropdown className="u-position-absolute" expanded={false} />}
          </div>
          <Breadcrumbs>
            <WorkflowsCrumbLink className="workflows-workflowDetailsHeader-workflowsCrumbLink" />
          </Breadcrumbs>
          <div>
            <ModalAnchor open={this.state.isSettingsModalOpen}>
              <WorkflowSettingsModal
                onRequestClose={this._handleRequestCloseModal}
                workflow={this.props.workflow}
              />
            </ModalAnchor>
            <span className="workflows-workflowHeader-header">
              <Tooltip placement={TooltipPlacement.BOTTOM} title={this.props.workflow.humanId}>
                <Header1 className="workflows-workflowDetailsHeader-workflowHumanId">
                  {this.props.workflow.humanId}
                </Header1>
              </Tooltip>
              {this._shouldShowSettings() && <SettingsIcon onClick={this._handleClickSettingsIcon} />}
            </span>
            <Tooltip placement={TooltipPlacement.BOTTOM} title={this.props.workflow.name}>
              <div className="workflows-workflowDetailsHeader-workflowName">{this.props.workflow.name}</div>
            </Tooltip>
            <Tooltip placement={TooltipPlacement.BOTTOM} title={this.props.workflow.description || ''}>
              <WorkflowDescription
                description={this.props.workflow.description}
                onClickMore={this._handleClickSettingsIcon}
              />
            </Tooltip>
          </div>
          <div className="u-vertical-flex u-justify-content-end u-flex-grow">
            <div className="workflows-workflowDetailsHeader-buttonRow">
              {this._wasInitializedFromRequest() && (
                <Button
                  buttonStyle={ButtonStyle.TERTIARY_GRAY}
                  onClick={this._handleUpdateFulfillmentStatuses}
                  size={ButtonSize.SMALL}
                  text="Update Requests"
                />
              )}
              {BenchlingConfig.LEGACY_WORKFLOWS_ENABLE_BULK_SEND_STAGE_ENTRIES_FOR_REVIEW &&
                !BenchlingConfig.ENABLE_ELN_ESIGNATURE && (
                  <Button
                    disabled={!this._getAllStageEntryIds().length}
                    buttonStyle={ButtonStyle.SECONDARY}
                    size={ButtonSize.MEDIUM}
                    onClick={() =>
                      BulkCreateStageEntryReviewModal.open({
                        stageEntryIds: this._getAllStageEntryIds(),
                        workflowId: this.props.workflow.id,
                      })
                    }
                    text="Send all entries for review"
                  />
                )}
            </div>
          </div>
        </div>
        <div className="workflows-workflowDetailsHeader-right">
          <AutoSizer disableHeight={true}>
            {({width}) => (
              <div className="workflows-workflowDetailsHeader-workflowVisualizationContainer">
                <WorkflowVisualization
                  containerWidth={width}
                  workflowStages={this.props.workflowStages}
                  workflowId={this.props.workflow.id}
                  stageIdToStageRuns={this.props.stageIdToStageRuns}
                  stageIdToStageConfiguration={this.props.stageIdToStageConfiguration}
                  stageRunGraph={this.props.stageRunGraph}
                  isWorkflowEditable={this.props.workflow.permissions.writable}
                />
              </div>
            )}
          </AutoSizer>
          <div className="workflows-workflowDetailsHeader-toggleCollapseArrow">
            {this.props.isExpanded && <ToggleDropdown expanded={true} />}
          </div>
        </div>
      </div>
    );
  }
}

export default WorkflowDetailsHeader;
